// src/features/users/hooks/useUserForm.ts
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usersApi } from '../services/usersApi';
import { statusOptions, UserData } from '..';
import { useToast } from '../../../common/context/ToastContext';
import { finishFormSave, useFormDirty } from '../../../common';

export const useUserForm = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { showToast } = useToast();
	const [user, setUser] = useState<UserData>({
		id: 0,
		login: '',
		password: '',
		email: '',
		firstName: '',
		name: '',
		status: 'active',
		roles: ['ROLE_USER'],
		permissions: [],
	});
	const [loading, setLoading] = useState(!!id && id !== 'new');
	const [passwordError, setPasswordError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { isDirty, markClean } = useFormDirty(user, !loading);

	// Fetch user si id existant
	useEffect(() => {
		if (!id || id === 'new') return setLoading(false);

		usersApi
			.get(Number(id), localStorage.getItem('token') || '')
			.then((data) => setUser({ ...data, password: '' }))
			.catch(() => showToast("Impossible de charger l'utilisateur", 'error'))
			.finally(() => setLoading(false));
	}, [id]);

	// Handle input changes
	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
			| { target: { name: string; value: string } }
	) => {
		const { name, value } = e.target;

		// Update user state, prev is the last state of user
		setUser((prev) => {
			return { ...prev, [name]: value };
		});

		if (name === 'password') {
			const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
			setPasswordError(
				regex.test(value)
					? null
					: 'Min. 8 caractères avec maj, min, chiffre et caractère spécial'
			);
		}
	};

	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		// Si name est status, on doit mettre à jour l'objet complet
		if (name === 'status') {
			const selectedOption = statusOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setUser({
					...user,
					status: selectedOption.key as UserData['status'],
				});
			}
		}
		// Si name est roles, on doit mettre à jour l'objet complet
		else if (name === 'roles') {
			setUser({
				...user,
				roles:
					typeof value === 'string'
						? value.split(',').map((r) => r.trim())
						: value,
			});
		}
	};

	// Construit le payload API à partir de l'état courant du formulaire
	const buildApiPayload = () => {
		const payload = { ...user };
		if (id && !payload.password) {
			delete payload.password;
		}
		const permissionsPayload = (payload.permissions ?? []).map((perm) => ({
			module: `/api/modules/${perm.module.id}`,
			action: perm.action,
		}));
		return {
			...payload,
			permissions: permissionsPayload,
		} as Record<string, unknown>;
	};

	const handleSave = async (shouldLeave: boolean) => {
		// Get token from localStorage
		const token = localStorage.getItem('token');
		if (!token) return;

		// Block creation if password is invalid
		if (!id && passwordError) {
			showToast('Veuillez corriger le mot de passe', 'warning');
			return;
		}

		setIsSubmitting(true);
		try {
			// Call appropriate API function
			const apiPayload = buildApiPayload();
			// Navigate back to user list
			const onLeave = () => navigate('/app/users');

			// Mise à jour sans quitter la page (uniquement en mode édition)
			if (id) {
				await usersApi.update(Number(id), apiPayload, token);
				finishFormSave(shouldLeave, { onLeave, markClean });
			} else {
				const created = await usersApi.create(apiPayload, token);
				finishFormSave(shouldLeave, {
					onLeave,
					markClean,
					stayOnEdit: created?.id
						? () =>
								navigate(`/app/users/${created.id}/edit`, {
									replace: true,
								})
						: undefined,
				});
			}
		} catch (err) {
			console.error(err);
			const errorMsg =
				typeof err === 'object' && err !== null && 'message' in err
					? (err as { message?: string }).message
					: String(err);
			showToast(
				`Erreur lors de la sauvegarde${errorMsg ? ': ' + errorMsg : ''}`,
				'error'
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Handle form submission (avec redirection vers la liste)
	const handleSubmit = async (e: React.FormEvent) => {
		// Prevent default form submission
		e.preventDefault();
		await handleSave(true);
	};

	return {
		user,
		loading,
		passwordError,
		isSubmitting,
		isDirty,
		handleChange,
		handleChangeSelect,
		handleSubmit,
		handleSave,
		navigate,
		id,
		setUser,
	};
};

export default useUserForm;
